import { Eye, EyeOff, Plus, Trash2, X } from "lucide-react";
import { useRef, useState } from "react";
import type { CurvaDocument } from "@/document/model/document";
import { oklchToCss } from "@/document/model/color";
import type { NodeEffect, NodeStyle } from "@/document/model/styles";
import { AppliedStyleRow } from "./AppliedStyleRow";
import { AnchoredMenu } from "./AnchoredMenu";
import { effectSummary, effectTypeLabel, effectOptions } from "./effectLabels";
import { PanelSection, PanelSectionIconButton, PanelSectionStyleButton } from "./PanelSection";
import { type EffectType, styleWithAddedEffect, styleWithUpdatedEffect, styleWithoutEffect } from "./paintStyles";

export function EffectsSection({
  style,
  onStyleChange,
  onEditEffect,
  document,
  appliedStyleId,
  onOpenStyles,
  onDetachStyle,
  onEditStyle,
}: {
  style: NodeStyle;
  onStyleChange: (style: NodeStyle) => void;
  onEditEffect: (effectId: string) => void;
  document?: CurvaDocument;
  appliedStyleId?: string;
  onOpenStyles?: () => void;
  onDetachStyle?: () => void;
  onEditStyle?: () => void;
}) {
  const effects = style.effects ?? [];
  const [addOpen, setAddOpen] = useState(false);
  const addAnchorRef = useRef<HTMLDivElement>(null);

  function addEffect(type: EffectType): void {
    const nextStyle = styleWithAddedEffect(style, type);
    onStyleChange(nextStyle);
    setAddOpen(false);
    const added = nextStyle.effects?.at(-1);
    if (added) {
      onEditEffect(added.id);
    }
  }

  return (
    <PanelSection
      title="Effects"
      headerRight={(
        <div className="flex items-center gap-0.5">
          {onOpenStyles ? <PanelSectionStyleButton label="Open effect styles" onClick={onOpenStyles} /> : null}
          <div ref={addAnchorRef}>
            <PanelSectionIconButton
              label="Add effect"
              expanded={addOpen}
              onClick={() => setAddOpen(open => !open)}
            >
              {addOpen ? <X className="size-3.5" aria-hidden="true" /> : <Plus className="size-3.5" aria-hidden="true" />}
            </PanelSectionIconButton>
          <AnchoredMenu open={addOpen} onOpenChange={setAddOpen} anchorRef={addAnchorRef} className="min-w-40">
            {effectOptions.map(option => (
              <button
                key={option.type}
                type="button"
                onClick={() => addEffect(option.type)}
                className="h-8 w-full rounded px-2 text-left text-[12px] text-slate-300 hover:bg-white/[0.08] hover:text-slate-100"
              >
                {option.label}
              </button>
            ))}
          </AnchoredMenu>
          </div>
        </div>
      )}
    >
      {appliedStyleId && document ? (
        <AppliedStyleRow
          document={document}
          styleId={appliedStyleId}
          kind="effects"
          onDetach={() => onDetachStyle?.()}
          onEdit={onEditStyle}
        />
      ) : effects.length > 0 ? (
        <div className="grid gap-1">
          {effects.map(effect => (
            <EffectRow
              key={effect.id}
              effect={effect}
              onEdit={() => onEditEffect(effect.id)}
              onToggleVisible={() => onStyleChange(styleWithUpdatedEffect(style, effect.id, { visible: !effect.visible }))}
              onRemove={() => onStyleChange(styleWithoutEffect(style, effect.id))}
            />
          ))}
        </div>
      ) : null}
    </PanelSection>
  );
}

function EffectRow({
  effect,
  onEdit,
  onToggleVisible,
  onRemove,
}: {
  effect: NodeEffect;
  onEdit: () => void;
  onToggleVisible: () => void;
  onRemove: () => void;
}) {
  const shadowColor = effect.type === "dropShadow" || effect.type === "innerShadow" ? effect.color : undefined;

  return (
    <div className={`flex h-7 items-center gap-1.5 rounded bg-white/[0.04] px-1.5 ${effect.visible ? "" : "opacity-50"}`}>
      <button
        type="button"
        aria-label={effect.visible ? "Hide effect" : "Show effect"}
        onClick={event => {
          event.stopPropagation();
          onToggleVisible();
        }}
        className="grid size-5 shrink-0 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-200"
      >
        {effect.visible ? <Eye className="size-3.5" aria-hidden="true" /> : <EyeOff className="size-3.5" aria-hidden="true" />}
      </button>
      <button
        type="button"
        onClick={onEdit}
        className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
      >
        {shadowColor ? (
          <span
            className="size-5 shrink-0 rounded-sm border border-white/20"
            style={{ background: oklchToCss(shadowColor) }}
            aria-hidden="true"
          />
        ) : (
          <span className="size-5 shrink-0 rounded-sm border border-white/20 bg-white/10" aria-hidden="true" />
        )}
        <span className="min-w-0 truncate text-[11px] text-slate-200">{effectTypeLabel(effect.type)}</span>
        <span className="min-w-0 truncate text-[10px] text-slate-500">{effectSummary(effect)}</span>
      </button>
      <button
        type="button"
        aria-label="Remove effect"
        onClick={event => {
          event.stopPropagation();
          onRemove();
        }}
        className="grid size-5 shrink-0 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-200"
      >
        <Trash2 className="size-3" aria-hidden="true" />
      </button>
    </div>
  );
}
